import { FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { PlannerSecondaryPanelType } from 'store/dealManagement/types';
import { changeDealData, changePlannerSecondaryPanelType } from 'store/dealManagement/reducer';

import ToggleSwitch from 'lib/ToggleSwitch';
import { Label } from 'lib/Label';
import Button, { Color } from 'lib/Button';
import Chip from 'lib/Chip';
import Tooltip, { TooltipDirection } from 'lib/Tooltip';

import plusSVG from 'assets/icons/plus.svg';

import { DirectSalesExclusionFlag } from 'components/common/types/DirectSalesDeal.types';
import { Store } from 'components/common/types/Store.types';
import { Size, TextPriority } from 'lib/Chip/Chip.types';

const Duplication: FC = () => {
  const dispatch = useDispatch();

  const duplicationExclusion = useSelector((state: Store) => state.dealManagement.commonDeal.duplicationExclusion);
  const organisations = useSelector((state: Store) => state.dealManagement.commonDeal.exclusions.organisations);
  const productCategories = useSelector((state: Store) => state.dealManagement.commonDeal.exclusions.productCategories);

  const isDuplicationOn = duplicationExclusion === DirectSalesExclusionFlag.ON;

  const onDuplicationChange = (): void => {
    dispatch(
      changeDealData({
        duplicationExclusion: isDuplicationOn ? DirectSalesExclusionFlag.OFF : DirectSalesExclusionFlag.ON,
      }),
    );
  };

  const onRemove = (organisationsToRemove: string[]): void => {
    const values = new Set(organisationsToRemove);
    const filtered = organisations.filter((org) => !values.has(org.name));
    dispatch(changeDealData({ exclusions: { organisations: filtered, productCategories } }));
  };

  return (
    <div className="w-full pb-1 border-b border-neutral-950-opacity-10">
      <div className="flex w-full justify-between items-center py-1">
        <Label id="duplication-label" label="Duplication" className="mb-0 font-semibold" />
        <ToggleSwitch
          isChecked={isDuplicationOn}
          onChange={() => onDuplicationChange()}
          dataTestId="duplication-switch"
        />
      </div>
      {isDuplicationOn && (
        <div className="flex w-full justify-between items-start">
          <Button
            color={Color.TRANSPARENT}
            label="Add"
            svg={plusSVG}
            onClick={() => dispatch(changePlannerSecondaryPanelType(PlannerSecondaryPanelType.DUPLICATION))}
          />
          <div className="justify-items-end">
            {organisations.map(({ code, name }) => {
              return (
                <div className="pb-2" key={code}>
                  <Tooltip key={code} tooltip={name} direction={TooltipDirection.BOTTOM}>
                    <Chip
                      label={name}
                      size={Size.MEDIUM}
                      textPriority={TextPriority.EQUAL}
                      onRemove={() => onRemove([name])}
                    />
                  </Tooltip>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Duplication;
